import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useEffect, useLayoutEffect, useMemo, useReducer, useState, useSyncExternalStore } from "react";
import { fetchPortal, fetchPortalBoot, PortalApiError, type PortalBootResponse } from "@/lib/api";
import { useLocale, resolveSupportedLocale } from "@/lib/i18n/I18nProvider";
import { injectPortalHead } from "@/lib/headInjection";
import {
  LOADING_HOLD_MS,
  SPINNER_CAP_MS,
  SPINNER_GRACE_MS,
  pickSpinnerInk,
  sanitizeImageUrl,
  withPortalDefaults,
} from "@/lib/portal-utils";
import {
  dismissBootScreen,
  getBootChannel,
  readBootVersion,
  subscribeBootChannel,
  type PortalBootChannel,
} from "@/lib/boot-screen";
import { PortalClient } from "@/PortalClient";
import { PlatformImprintLink } from "@/components/PlatformImprintLink";

type ErrorKind =
  | "invalidPortalToken"
  | "pluginDisabled"
  | "guestRemoved"
  | "eventRemoved"
  | "network"
  | "unknown";

function classifyError(err: unknown): ErrorKind {
  if (err instanceof PortalApiError) {
    if (err.code === "invalidPortalToken") return "invalidPortalToken";
    if (err.code === "pluginDisabled") return "pluginDisabled";
    if (err.code === "guestRemoved") return "guestRemoved";
    // M36: the worker's 404 {message:"eventRemoved"} (event deleted after
    // mint) is a permanent, explainable state — give it its own screen
    // instead of folding it into "unknown".
    if (err.code === "eventRemoved") return "eventRemoved";
    return "unknown";
  }
  if (err instanceof TypeError) return "network";
  return "unknown";
}

export function PortalPage() {
  const { token = "" } = useParams<{ token: string }>();
  const t = useTranslations("errors");
  const { locale, setLocale } = useLocale();

  const q = useQuery({
    queryKey: ["portal", token],
    queryFn: () => fetchPortal(token),
    enabled: !!token,
  });
  // The loading animation + page background, requested alongside the data so
  // the spinner can be replaced the moment this (cheaper) call lands. No
  // retry: by the time a retry fired the data would be there. A failure
  // (older worker, network) just keeps the spinner — never an error screen.
  const boot = useQuery({
    queryKey: ["portal-boot", token],
    queryFn: () => fetchPortalBoot(token),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  // The inline script's channel (index.html → src/lib/boot-screen.ts): it may
  // already hold the preloaded animation, or have painted the screen before
  // React existed. Its version is the snapshot — every mutation (image
  // state, paint, dismissal) re-renders this page.
  const channel = getBootChannel(token);
  const [subscribe, getSnapshot] = useMemo(
    () =>
      channel
        ? [(cb: () => void) => subscribeBootChannel(channel, cb), () => readBootVersion(channel)]
        : [noopSubscribe, zero],
    [channel],
  );
  useSyncExternalStore(subscribe, getSnapshot);

  // The hold: once the animation has painted (by the script or by React's
  // own <img>), the screen stays LOADING_HOLD_MS even if the data landed
  // earlier — one turn of the loop instead of a flash. Nothing painted → no
  // hold, the data wins at once.
  const [reactPaintedAt, setReactPaintedAt] = useState(0);
  const settled = !!q.data || q.isError;
  const paintedAt = channel?.paintedAt || reactPaintedAt;
  const holding = useLoadingHold(settled, paintedAt);
  const showLoading = !!token && (q.isLoading || holding);

  // Take the script's overlay down in the same frame the portal (or an error
  // screen, or the tokenless screen) first paints — a layout effect, so the
  // guest never sees both. Idempotent; a no-op without a channel.
  useLayoutEffect(() => {
    if (!showLoading) dismissBootScreen();
  }, [showLoading]);

  // Merge unset/null fields onto the shared default baseline so the live
  // portal renders identically to the builder preview, deterministically and
  // independent of the guest's OS dark-mode (see withPortalDefaults).
  const portalConfig = q.data ? withPortalDefaults(q.data.portalConfig) : undefined;

  // Pick the UI language from the portal data, not the browser: prefer the
  // guest's own stored language, then the event's configured defaultLocale.
  // Only override when one of them resolves to a bundled locale — otherwise
  // keep the bootstrap (browser-detected) locale used for the loading/error
  // screens. en-GB remains the floor via the dynamic-import catch in the
  // provider.
  useEffect(() => {
    if (!q.data) return;
    const resolved =
      resolveSupportedLocale(q.data.guestLanguage) ??
      resolveSupportedLocale(portalConfig?.defaultLocale);
    if (resolved && resolved !== locale) setLocale(resolved);
  }, [q.data, portalConfig?.defaultLocale, locale, setLocale]);

  // Apply branding (title, favicon, og:*, customCss) and the Art. 50 KI-VO
  // `<meta name="ai-disclosure">` (top-level `aiDisclosure`) once data lands.
  useEffect(() => {
    if (q.data) {
      injectPortalHead(portalConfig, q.data.eventName, locale, q.data.variables, q.data.aiDisclosure);
    }
  }, [q.data, q.data?.aiDisclosure, portalConfig, locale]);

  if (!token) {
    return (
      <ErrorScreen message={t("invalidPortalToken")} action={t("invalidPortalTokenAction")} />
    );
  }

  if (showLoading) {
    return (
      <LoadingScreen
        boot={boot.data}
        bootPending={boot.isPending}
        channel={channel}
        onPainted={setReactPaintedAt}
      />
    );
  }

  if (q.data) {
    return (
      <PortalClient
        token={token}
        guestName={q.data.guestName}
        eventName={q.data.eventName}
        attachments={q.data.attachments}
        portalConfig={portalConfig}
        variables={q.data.variables}
        ticket={q.data.ticket}
        locale={locale}
      />
    );
  }

  const errorKind = classifyError(q.error);
  return (
    <ErrorScreen
      message={t(errorKind)}
      action={
        errorKind === "invalidPortalToken"
          ? t("invalidPortalTokenAction")
          : errorKind === "guestRemoved" || errorKind === "eventRemoved"
            ? t("guestRemovedAction")
            : undefined
      }
    />
  );
}

/** Exported for its test only. */
export function ErrorScreen({ message, action }: { message: string; action?: string | undefined }) {
  return (
    <div className="flex items-center justify-center h-dvh">
      <div className="text-center max-w-md px-4">
        <p className="text-base mb-4" data-testid="portal-error-message">
          {message}
        </p>
        {action && (
          <p className="text-sm" style={{ color: "var(--portal-fg-muted)" }} data-testid="portal-error-action">
            {action}
          </p>
        )}
        {/* A guest whose link died still stands on Veltric's servers. */}
        <PlatformImprintLink style={{ color: "var(--portal-fg-muted)" }} />
      </div>
    </div>
  );
}

const noopSubscribe = () => () => {};
const zero = () => 0;

/**
 * `true` once `Date.now()` has reached `deadline` (null = no deadline). Arms a
 * timer for the remainder and re-renders when it fires; a deadline that moves
 * re-arms. Everything reads `Date.now()` (never `performance.now()`) so the
 * inline script, this page and the test clock share one time base.
 *
 * `fired` is in the dependencies ON PURPOSE. Timers run on the monotonic
 * clock, `Date.now()` on the wall clock: when the wall clock steps back (NTP,
 * a phone resyncing after wake) the timer fires while `Date.now()` is still
 * short of the deadline, `passed` stays false, and without a re-arm nothing
 * would ever fire again — the guest sat behind the loading overlay until an
 * unrelated re-render came by (seen in a real browser). Each fire re-arms
 * for the remainder until the wall clock has actually caught up.
 */
function useDeadlinePassed(deadline: number | null): boolean {
  const [fired, rerender] = useReducer((n: number) => n + 1, 0);
  const passed = deadline !== null && Date.now() >= deadline;
  useEffect(() => {
    if (deadline === null || passed) return;
    const id = setTimeout(rerender, Math.max(1, deadline - Date.now()));
    return () => clearTimeout(id);
  }, [deadline, passed, fired]);
  return passed;
}

/** The loading screen is held while the data is there but the animation painted < LOADING_HOLD_MS ago. */
function useLoadingHold(settled: boolean, paintedAt: number): boolean {
  const deadline = settled && paintedAt > 0 ? paintedAt + LOADING_HOLD_MS : null;
  const passed = useDeadlinePassed(deadline);
  return deadline !== null && !passed;
}

/** `#rgb` / `#rrggbb` / `#rrggbbaa` only — anything else keeps the default background. */
const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;

type ImageState = "pending" | "loaded" | "failed";

/**
 * The pre-data screen. While `/boot` is unanswered it is blank for
 * SPINNER_GRACE_MS, then the SPA's spinner on the default background — a fast
 * boot with an animation never flashes the spinner. Once boot lands with an
 * animation, the operator's background alone while the bytes load (no
 * spinner: the GIF is about to appear in its place), the animation once
 * loaded, and the spinner only as a fallback — when the image FAILED (deleted
 * from the library, R2 blip) or after SPINNER_CAP_MS without bytes, so a slow
 * network never leaves the guest on a bare coloured screen for good. Boot
 * without an animation: the spinner at once. The spinner's ink is picked by
 * MEASURED contrast against that background (pickSpinnerInk) — the muted
 * default really does vanish on a mid-grey, and a luma flip cannot see it.
 *
 * Two lanes for the image. With the inline channel (index.html painted first,
 * src/lib/boot-screen.ts) the script's preloaded element is authoritative:
 * once its overlay is up this component renders NOTHING (no second live
 * region, no second image — a second `<img>` would restart the GIF), and
 * until then only the coloured container with the spinner rule above, never
 * an `<img>` of its own. Without a channel (tests, the legacy `/portal/:token`
 * route) the image is drawn here, `hidden` until `onLoad` (a `display:none`
 * image is not animated, so the reveal is frame 0), and `onPainted` reports
 * the paint for the hold.
 *
 * One live region names the whole thing; the `<img alt="">` inside is
 * decorative and carries no role of its own (an image with an empty alt may
 * not, per ARIA in HTML).
 *
 * ⚠️ MIRROR TWIN — the builder preview draws this screen as its "page
 * loading" state (main-frontend PortalPreview.tsx). Keep the markup in step;
 * the timings live in the shared portal-utils twin.
 */
export function LoadingScreen({
  boot,
  bootPending = false,
  channel = null,
  onPainted,
}: {
  boot: PortalBootResponse | undefined;
  /** `/boot` still unanswered → the grace applies. */
  bootPending?: boolean;
  channel?: PortalBootChannel | null;
  /** The animation painted (React lane only; the channel carries its own `paintedAt`). */
  onPainted?: (at: number) => void;
}) {
  // The channel is authoritative when present — otherwise React would wait
  // for a paint the script never attempts (a URL it rejected).
  const image = channel ? (channel.image?.url ?? null) : sanitizeImageUrl(boot?.loadingImageUrl);
  const bg = boot?.backgroundColor && HEX_COLOR.test(boot.backgroundColor) ? boot.backgroundColor : undefined;
  const [mountedAt] = useState(() => Date.now());
  // React lane, per image URL: pending → loaded | failed, and when the URL was
  // first seen (the cap clock). Keyed by the URL so a different animation (new
  // boot answer) starts pending again instead of inheriting the old verdict.
  const [track, setTrack] = useState<{ url: string; state: ImageState; startedAt: number } | null>(null);
  if (!channel && image && track?.url !== image) {
    setTrack({ url: image, state: "pending", startedAt: Date.now() });
  }
  const imageState: ImageState = channel
    ? (channel.image?.state ?? "pending")
    : image && track?.url === image
      ? track.state
      : "pending";
  const imageStartedAt = channel ? (channel.image?.startedAt ?? 0) : (track?.startedAt ?? 0);

  const graceOver = useDeadlinePassed(image === null && bootPending ? mountedAt + SPINNER_GRACE_MS : null);
  const capOver = useDeadlinePassed(
    image !== null && imageState === "pending" && imageStartedAt > 0 ? imageStartedAt + SPINNER_CAP_MS : null,
  );

  // The script's overlay is up: it IS the loading screen.
  if (channel && channel.paintedAt > 0 && !channel.taken) return null;

  const showSpinner = image === null ? !bootPending || graceOver : imageState === "failed" || capOver;
  const showImage = !channel && !!image && imageState !== "failed";
  return (
    <div
      className="flex items-center justify-center h-dvh"
      style={bg ? { backgroundColor: bg } : undefined}
      data-testid="portal-loading-screen"
    >
      <div role="status" aria-label="Loading" className="flex items-center justify-center">
        {showSpinner && (
          <div
            className="h-8 w-8 rounded-full border-2 border-current border-t-transparent animate-spin"
            style={{ color: pickSpinnerInk(bg) }}
            data-testid="portal-loading-spinner"
          />
        )}
        {showImage && (
          <img
            src={image}
            alt=""
            className="max-h-40 max-w-[70vw] object-contain"
            hidden={imageState !== "loaded"}
            onLoad={() => {
              setTrack((t) => (t && t.url === image ? { ...t, state: "loaded" } : t));
              onPainted?.(Date.now());
            }}
            onError={() => setTrack((t) => (t && t.url === image ? { ...t, state: "failed" } : t))}
            data-testid="portal-loading-image"
          />
        )}
      </div>
    </div>
  );
}
